import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Alert, Radio, RadioGroup, FormControlLabel, LinearProgress, CircularProgress } from "@mui/material";
import BarometerApi, { BarometerMeta, BarometerAnswers } from "../api/BarometerApi";

// Public, anonymous respond page for the organisation barometer (v2, modular).
// - No login required; the shared wave link is the only "credential".
// - MDR-safe: after submitting we show a UNIFORM signpost (identical for every
//   answer combination) — deliberately NO score, NO banding, NO interpretation.
// - Modules (the wave decides which are shown):
//   welzijn      = Cantril-ladder, 2 items, 0..10
//   burnout      = BAT-12, 12 items, 1..5
//   werkbeleving = eigen Carefunded-werkitems, 7 items, 1..5 — transparently
//                  labelled as own questions, not a validated clinical instrument.
const CANTRIL_COUNT = 2;
const BAT12_COUNT = 12;
const WERK_COUNT = 7;

function ItemCard({ index, text, value, options, onChange }: {
  index: number; text: string; value: number;
  options: { value: number; label: string }[];
  onChange: (v: number) => void;
}) {
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3 }, mb: 2, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
      <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{index}. {text}</Typography>
      <RadioGroup value={String(value)} onChange={(e) => onChange(Number(e.target.value))}>
        {options.map((o) => (
          <FormControlLabel key={o.value} value={String(o.value)} control={<Radio size="small" />} label={o.label} />
        ))}
      </RadioGroup>
    </Box>
  );
}

// Cantril ladder item: a horizontal 0..10 row with the two anchors spelled out.
function LadderCard({ index, text, value, onChange }: {
  index: number; text: string; value: number; onChange: (v: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: { xs: 2.5, sm: 3 }, mb: 2, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)" }}>
      <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{index}. {text}</Typography>
      <RadioGroup row value={String(value)} onChange={(e) => onChange(Number(e.target.value))} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {Array.from({ length: 11 }, (_, v) => (
          <FormControlLabel key={v} value={String(v)} control={<Radio size="small" />} label={String(v)}
            labelPlacement="top" sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: ".85rem" } }} />
        ))}
      </RadioGroup>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography sx={{ fontSize: ".8rem", color: "text.secondary" }}>0 — {t("barometer.cantrilLow")}</Typography>
        <Typography sx={{ fontSize: ".8rem", color: "text.secondary" }}>10 — {t("barometer.cantrilHigh")}</Typography>
      </Box>
    </Box>
  );
}

export default function BarometerRespond() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [meta, setMeta] = useState<BarometerMeta | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [cantril, setCantril] = useState<number[]>(new Array(CANTRIL_COUNT).fill(-1));
  const [bat12, setBat12] = useState<number[]>(new Array(BAT12_COUNT).fill(-1));
  const [werk, setWerk] = useState<number[]>(new Array(WERK_COUNT).fill(-1));
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (token) BarometerApi.meta(token).then(setMeta).catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, sm: 10 } }}>
        <Alert severity="warning">{t("barometer.notFound")}</Alert>
      </Container>
    );
  }
  if (!meta) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  const modules = meta.modules || [];
  const hasWelzijn = modules.includes("welzijn");
  const hasBurnout = modules.includes("burnout");
  const hasWerk = modules.includes("werkbeleving");

  const bat12Options = [1, 2, 3, 4, 5].map((v) => ({ value: v, label: t(`barometer.batScale.s${v}`) }));
  const werkOptions = [1, 2, 3, 4, 5].map((v) => ({ value: v, label: t(`barometer.werkScale.s${v}`) }));

  const answeredOf = (arr: number[], enabled: boolean) => (enabled ? arr.filter((a) => a >= 0).length : 0);
  const answered = answeredOf(cantril, hasWelzijn) + answeredOf(bat12, hasBurnout) + answeredOf(werk, hasWerk);
  const total = (hasWelzijn ? CANTRIL_COUNT : 0) + (hasBurnout ? BAT12_COUNT : 0) + (hasWerk ? WERK_COUNT : 0);
  const allAnswered = total > 0 && answered === total;

  // Continuous numbering across the shown sections.
  const cantrilBase = 0;
  const batBase = hasWelzijn ? CANTRIL_COUNT : 0;
  const werkBase = batBase + (hasBurnout ? BAT12_COUNT : 0);

  const submit = async () => {
    if (!token || !allAnswered) return;
    setSubmitErr("");
    setSubmitting(true);
    try {
      const answers: BarometerAnswers = {};
      if (hasWelzijn) answers.cantril = cantril;
      if (hasBurnout) answers.bat12 = bat12;
      if (hasWerk) answers.werk = werk;
      await BarometerApi.respond(token, answers);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      setSubmitErr(e?.response?.status === 409 ? t("barometer.closed") : t("barometer.submitError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", pb: 10 }}>
      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
        <Typography variant="h1" sx={{ fontSize: { xs: "2rem", sm: "2.5rem" }, mb: 0.5 }}>{t("barometer.respondTitle")}</Typography>
        <Typography sx={{ color: "text.secondary", mb: 2 }}>{meta.orgName} — {meta.title}</Typography>

        {done ? (
          <Box sx={{ mt: 3 }}>
            {/* Uniform closing card — identical for every answer combination.
                No score, no banding, no interpretation (MDR). */}
            <Box sx={{ p: { xs: 3, sm: 4 }, borderRadius: "var(--radius-lg)", bgcolor: "var(--surface)", border: "1px solid var(--border)", boxShadow: "var(--shadow-sm)" }}>
              <Typography variant="h2" sx={{ fontSize: "1.6rem", mb: 1 }}>{t("barometer.thanksTitle")}</Typography>
              <Typography sx={{ mb: 2 }}>{t("barometer.thanksBody")}</Typography>
              <Typography sx={{ mb: 2 }}>{t("barometer.thanksTalk")}</Typography>
              <Button variant="contained" onClick={() => navigate("/start")}>{t("barometer.ctaMatch")}</Button>
            </Box>
            {/* Crisis block — always visible on the closing screen. */}
            <Alert severity="warning" sx={{ mt: 3 }}>
              {t("selftest.crisisIntro")}{" "}
              <Box component="a" href="tel:1813" sx={{ color: "inherit", fontWeight: 600 }}>{t("selftest.crisis1813")}</Box>{" "}
              {t("selftest.crisis112")}
            </Alert>
          </Box>
        ) : meta.status !== "open" ? (
          <Alert severity="info" sx={{ mt: 2 }}>{t("barometer.closed")}</Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>{t("barometer.anonIntro", { k: meta.kThreshold })}</Alert>
            <LinearProgress variant="determinate" value={(answered / total) * 100} sx={{ height: 8, borderRadius: 999, bgcolor: "var(--primary-100)", mb: 4, "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "primary.main" } }} />

            {hasWelzijn && (
              <>
                <Typography variant="h2" sx={{ fontSize: "1.3rem", mb: 0.5 }}>{t("barometer.cantrilTitle")}</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".95rem", mb: 2 }}>{t("barometer.cantrilIntro")}</Typography>
                {Array.from({ length: CANTRIL_COUNT }, (_, i) => (
                  <LadderCard key={`c${i}`} index={cantrilBase + i + 1} text={t(`barometer.cantril.i${i + 1}`)} value={cantril[i]}
                    onChange={(v) => setCantril((a) => { const n = [...a]; n[i] = v; return n; })} />
                ))}
              </>
            )}

            {hasBurnout && (
              <>
                <Typography variant="h2" sx={{ fontSize: "1.3rem", mt: hasWelzijn ? 4 : 0, mb: 0.5 }}>{t("barometer.batTitle")}</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".95rem", mb: 2 }}>{t("barometer.batLead")}</Typography>
                {Array.from({ length: BAT12_COUNT }, (_, i) => (
                  <ItemCard key={`b${i}`} index={batBase + i + 1} text={t(`barometer.bat12.i${i + 1}`)} value={bat12[i]} options={bat12Options}
                    onChange={(v) => setBat12((a) => { const n = [...a]; n[i] = v; return n; })} />
                ))}
              </>
            )}

            {hasWerk && (
              <>
                <Typography variant="h2" sx={{ fontSize: "1.3rem", mt: hasWelzijn || hasBurnout ? 4 : 0, mb: 0.5 }}>{t("barometer.werkTitle")}</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".95rem", mb: 1 }}>{t("barometer.werkLead")}</Typography>
                {/* Transparency: own questions, NOT a validated clinical instrument. */}
                <Typography sx={{ color: "text.secondary", fontSize: ".85rem", fontStyle: "italic", mb: 2 }}>{t("barometer.werkDisclaimer")}</Typography>
                {Array.from({ length: WERK_COUNT }, (_, i) => (
                  <ItemCard key={`k${i}`} index={werkBase + i + 1} text={t(`barometer.werk.i${i + 1}`)} value={werk[i]} options={werkOptions}
                    onChange={(v) => setWerk((a) => { const n = [...a]; n[i] = v; return n; })} />
                ))}
              </>
            )}

            {submitErr && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSubmitErr("")}>{submitErr}</Alert>}
            <Button variant="contained" size="large" fullWidth onClick={submit} disabled={!allAnswered || submitting} sx={{ mt: 2 }}>
              {t("barometer.submit")}
            </Button>
            <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mt: 1.5, textAlign: "center" }}>{t("barometer.noScoreNote")}</Typography>
          </>
        )}
      </Container>
    </Box>
  );
}
